import { useToast, AlertStatus, UseToastOptions } from "@chakra-ui/react";
import { useCallback } from "react";
import axios, { type AxiosError } from "axios";
import { isApiError } from "../utils/apiClients/ApiError";
import { isWafBlockError } from "../utils/apiClients/WafBlockError";
import { AppApiError, isObject, isValidAppApiError } from "../types";
import { useModifiedTranslation } from "./useModifiedTranslation";

function getApiErrorMessage(
  error: AppApiError,
  fallbackMessage: string,
  t: (key: string) => string,
): string {
  let message: string;
  if (isObject(error.detail)) {
    const tKey = `ApiError.${error.detail.type}`;
    message = t(tKey);
    if (message === tKey) {
      message = error.detail.msg || fallbackMessage;
    }
  } else if (Array.isArray(error.detail)) {
    const firstDetail = error.detail[0];
    if (firstDetail && isObject(firstDetail)) {
      message = firstDetail.msg || fallbackMessage;
    } else if (typeof firstDetail === "string") {
      message = firstDetail;
    } else {
      message = fallbackMessage;
    }
  } else {
    message = error.detail;
  }
  return message;
}

function getAxiosErrorMessage(
  err: AxiosError,
  t: (key: string) => string,
): string | undefined {
  const status = err.response?.status;
  const responseData = err.response?.data;
  if (isValidAppApiError(responseData)) {
    if (
      err.config?.url === "/account/password" &&
      status &&
      [400, 401, 403, 404, 422, 500, 504].includes(status)
    ) {
      return t(`ApiError.account_password.${status}`);
    }
    return getApiErrorMessage(responseData, err.message, t);
  }
  return err.message;
}

// status/title/description は本フックが算出するため受け取らない。duration 等の表示制御のみ上書き可能にする。
type ToastOverrides = Omit<UseToastOptions, "status" | "title" | "description">;

type ErrorToast = (
  e: unknown,
  toastStatus?: AlertStatus,
  options?: ToastOverrides,
) => void;

export function useToastMessage(): ErrorToast {
  const toast = useToast();
  const t = useModifiedTranslation();

  const getToastMessage = useCallback(
    (err: unknown): { title: string; message?: string } => {
      if (isApiError(err)) {
        const status = err.status;
        const responseData = err.body;

        let message: string | undefined = undefined;
        if (isValidAppApiError(responseData)) {
          message = getApiErrorMessage(responseData, err.message, t);
        } else {
          message = err.message;
        }

        return {
          title: `${status}: ${t("ApiError." + status)}`,
          message,
        };
      }

      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const message = getAxiosErrorMessage(err, t);

        return {
          title: status
            ? `${status}: ${t("ApiError." + status)}`
            : `${err.code}`,
          message,
        };
      }

      if (err instanceof Error) {
        return {
          title: err.name,
          message: err.message,
        };
      }

      if (typeof err === "string") {
        return {
          title: err,
        };
      }

      return {
        title: "Internal Error",
      };
    },
    [t],
  );

  return useCallback(
    (err: unknown, toastStatus = "error", options?: ToastOverrides) => {
      // WAFブロック時は既にalert表示とリロードを行っているため、
      // リロード中に同じ内容のトーストが出てちらつかないよう抑止する
      if (isWafBlockError(err)) return;

      const { title, message } = getToastMessage(err);

      toast({
        status: toastStatus,
        containerStyle: { whiteSpace: "pre-wrap" },
        isClosable: true,
        duration: 1500,
        ...options,
        title: title,
        description: message,
      });
    },
    [toast, getToastMessage],
  );
}
